// client/src/pages/BeginnerGuidePage.js
// A few plain questions that lead a newcomer to a suitable starting point.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Box, Button, Container, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import ProductCard from '../components/ProductCard';
import ProductSkeleton from '../components/ProductSkeleton';
import Seo from '../components/Seo';
import { API_BASE_URL } from '../utils/media';

const INTERESTS = [
  { value: 'RC Planes', label: 'Fly a plane' },
  { value: 'RC Helicopters', label: 'Fly a helicopter' },
  { value: 'Model Kits', label: 'Build a model' },
];
const BUDGETS = [
  { value: '2000', label: 'Up to R2 000' },
  { value: '5000', label: 'Up to R5 000' },
  { value: '', label: 'More than R5 000' },
];
const RADIO_CHOICES = [
  { value: 'no', label: "No, I'm starting from scratch" },
  { value: 'yes', label: 'Yes, I have a transmitter' },
];

const ADVICE = {
  'RC Planes': 'Look for a trainer with stabilisation: it\'s slower and forgiving while you learn. Practise on a simulator first, and fly somewhere open and away from people.',
  'RC Helicopters': 'Helicopters take longer to learn than planes. A small coaxial or stabilised model is the gentlest start, and a simulator will save you a lot of repairs.',
  'Model Kits': 'Start with a beginner kit with fewer parts. You\'ll also want glue, a hobby knife and paint if the kit doesn\'t include them.',
};

const Question = ({ title, options, value, onChange }) => (
  <Box sx={{ mb: 3 }}>
    <Typography variant="h6" sx={{ mb: 1 }}>{title}</Typography>
    <ToggleButtonGroup exclusive value={value} onChange={(e, next) => next !== null && onChange(next)} sx={{ flexWrap: 'wrap', gap: 1, '& .MuiToggleButtonGroup-grouped': { border: 1, borderColor: 'divider !important', ml: '0 !important', textTransform: 'none' } }}>
      {options.map((option) => <ToggleButton key={option.label} value={option.value}>{option.label}</ToggleButton>)}
    </ToggleButtonGroup>
  </Box>
);

const BeginnerGuidePage = () => {
  const [interest, setInterest] = useState(null);
  const [budget, setBudget] = useState(null);
  const [hasRadio, setHasRadio] = useState(null);
  const [results, setResults] = useState(null);
  const [broadened, setBroadened] = useState(false);

  const isKit = interest === 'Model Kits';
  const answered = interest !== null && budget !== null && (isKit || hasRadio !== null);

  useEffect(() => {
    if (!answered) {
      setResults(null);
      return undefined;
    }
    let cancelled = false;
    const search = async (params) => {
      const response = await fetch(`${API_BASE_URL}/api/products?${new URLSearchParams({ inStock: 'true', sort: 'price_asc', ...params })}`);
      return response.ok ? response.json() : [];
    };
    (async () => {
      setResults(null);
      const exact = { category: interest };
      if (budget) exact.priceMax = budget;
      if (!isKit) exact.buildType = hasRadio === 'yes' ? 'BNF' : 'RTF';
      let found = await search(exact);
      let wider = false;
      if (found.length === 0) {
        found = await search({ category: interest });
        wider = true;
      }
      if (!cancelled) {
        setResults(found.slice(0, 8));
        setBroadened(wider);
      }
    })().catch(() => { if (!cancelled) setResults([]); });
    return () => { cancelled = true; };
  }, [answered, interest, budget, hasRadio, isKit]);

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 6 } }}>
      <Seo
        title="Beginner's Guide to RC"
        description="New to RC? Answer three quick questions to find a good first plane, helicopter or model kit."
        canonicalPath="/beginner-guide"
      />
      <Typography variant="overline" color="primary" sx={{ display: 'block' }}>Beginner's guide</Typography>
      <Typography component="h1" sx={{ fontSize: { xs: '2rem', md: '3rem' }, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.1, mb: 1 }}>
        Find your first model
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 4, maxWidth: 620 }}>
        Answer a few quick questions and we'll show you what's in stock.
      </Typography>

      <Question title="What would you like to do?" options={INTERESTS} value={interest} onChange={setInterest} />
      <Question title="What's your budget?" options={BUDGETS} value={budget} onChange={setBudget} />
      {!isKit && <Question title="Do you already have a radio transmitter?" options={RADIO_CHOICES} value={hasRadio} onChange={setHasRadio} />}

      {answered && (
        <Box sx={{ mt: 4 }}>
          <Alert severity="info" sx={{ mb: 3 }}>{ADVICE[interest]}</Alert>
          {results === null && <ProductSkeleton count={4} />}
          {results !== null && results.length === 0 && (
            <Alert severity="warning">
              We don't have anything in stock for this right now. <Link to={`/?${new URLSearchParams({ category: interest })}`} style={{ color: 'inherit', fontWeight: 700 }}>Browse {interest}</Link>, or contact us and we'll help you choose.
            </Alert>
          )}
          {results !== null && results.length > 0 && (
            <>
              <Typography variant="h5" component="h2" sx={{ mb: 0.5 }}>{broadened ? `Nothing matched exactly, so here's our ${interest}` : 'Good places to start'}</Typography>
              <Typography color="text.secondary" sx={{ mb: 2.5 }}>Lowest price first.</Typography>
              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(auto-fill, minmax(230px, 1fr))' } }}>
                {results.map((product) => <ProductCard key={product.id} product={product} />)}
              </Box>
              <Stack direction="row" sx={{ mt: 3 }}>
                <Button component={Link} to={`/?${new URLSearchParams({ category: interest })}`} variant="outlined">See everything in {interest}</Button>
              </Stack>
            </>
          )}
        </Box>
      )}
    </Container>
  );
};

export default BeginnerGuidePage;
